import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Icon,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  SxProps,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { BiPlus, BiSolidCar } from 'react-icons/bi';
import { useGetManagedVehiclesQuery } from '../queries/getManagedVehicles.query.generated';
import { Mobility_Vehicles } from '@gql/base-types';
import { useRooftop } from '@store';
import MobilityVehiclesTable from './MobilityVehiclesTable';
import VehicleFormDialog from './VehicleFormDialog';

interface DialogState {
  open: boolean;
  mode: 'create' | 'edit';
  vehicle: Mobility_Vehicles | null;
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'available', label: 'Available' },
  { value: 'in_use', label: 'In Use' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'retired', label: 'Retired' },
];

interface MobilityVehiclesContentProps {
  /** The product-shell tab omits the heading, since the shell band and the tab strip already name
   *  the surface. */
  showTitle?: boolean;
}

/** Rendered by both the standalone `/service-vehicles` page and the Service Vehicles tab on the
 *  Mobile Service and Parts Delivery shells, so it carries no outer page chrome. */
const MobilityVehiclesContent = ({ showTitle = false }: MobilityVehiclesContentProps) => {
  const theme = useTheme();
  const clsx = useSxStyles(theme);
  const rooftop = useRooftop();
  const customerId = rooftop?.id || 0;

  const { data, loading, refetch } = useGetManagedVehiclesQuery({
    variables: { customerId },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [makeFilter, setMakeFilter] = useState('all');
  const [dialog, setDialog] = useState<DialogState>({ open: false, mode: 'create', vehicle: null });

  const allVehicles = useMemo(() => (data?.mobility_vehicles ?? []) as Mobility_Vehicles[], [data]);

  // Distinct makes for the make filter, derived from the current customer's vehicles.
  const makes = useMemo(
    () => Array.from(new Set(allVehicles.map(v => v.make).filter(Boolean))).sort(),
    [allVehicles],
  );

  const vehicles = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allVehicles.filter(v => {
      if (statusFilter !== 'all' && v.status !== statusFilter) return false;
      if (makeFilter !== 'all' && v.make !== makeFilter) return false;
      if (!term) return true;
      return [v.make, v.model, v.license_plate, v.vin, v.color]
        .filter(Boolean)
        .some(field => String(field).toLowerCase().includes(term));
    });
  }, [allVehicles, search, statusFilter, makeFilter]);

  const openCreate = () => setDialog({ open: true, mode: 'create', vehicle: null });
  const openEdit = (vehicle: Mobility_Vehicles) => setDialog({ open: true, mode: 'edit', vehicle });
  const closeDialog = () => setDialog(prev => ({ ...prev, open: false }));

  // Reset make filter if the selected make disappears (e.g. after a refetch).
  useEffect(() => {
    if (makeFilter !== 'all' && !makes.includes(makeFilter)) setMakeFilter('all');
  }, [makes, makeFilter]);

  const hasFilters = !!search || statusFilter !== 'all' || makeFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setMakeFilter('all');
  };

  return (
    <Box>
      {showTitle ? (
        <Box sx={{ mb: 2 }}>
          <Typography sx={clsx.titleText}>Service Vehicles</Typography>
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <TextField
          label='Search'
          placeholder='Search make, model, plate, VIN...'
          variant='outlined'
          size='small'
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Icon color='disabled' fontSize='small'>
                  search
                </Icon>
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: { sm: 220 } }}
        />
        <TextField
          select
          label='Status'
          size='small'
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
        >
          {STATUS_FILTERS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label='Make'
          size='small'
          value={makeFilter}
          onChange={e => setMakeFilter(e.target.value)}
          sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
        >
          <MenuItem value='all'>All Makes</MenuItem>
          {makes.map(make => (
            <MenuItem key={make} value={make}>
              {make}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant='contained'
          startIcon={<BiPlus />}
          onClick={openCreate}
          disabled={!customerId}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Add Vehicle
        </Button>
      </Box>

      {loading && !data ? <LinearProgress /> : null}

      <Paper variant='custom' style={{ overflow: 'hidden' }}>
        {vehicles.length ? (
          <MobilityVehiclesTable vehicles={vehicles} onEdit={openEdit} />
        ) : hasFilters ? (
          // Filtered / no-matches: text only, with a clear-filters affordance.
          <Box sx={clsx.emptyFiltered}>
            <Typography variant='body1' color='text.secondary'>
              No vehicles match your filters.
            </Typography>
            <Button size='small' onClick={clearFilters} sx={{ mt: 1 }}>
              Clear filters
            </Button>
          </Box>
        ) : (
          // Zero data: full empty state with icon badge + primary CTA.
          <Box sx={clsx.emptyZero}>
            <Box sx={clsx.emptyBadge}>
              <BiSolidCar size={48} />
            </Box>
            <Typography variant='h6' sx={{ fontWeight: 700, mt: 2 }}>
              No vehicles yet
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 280, mt: 0.5 }}>
              Add your dealership's mobility vehicles to assign them to delivery and service runs.
            </Typography>
            <Button variant='contained' startIcon={<BiPlus />} onClick={openCreate} sx={{ mt: 2 }}>
              Add First Vehicle
            </Button>
          </Box>
        )}
      </Paper>

      <VehicleFormDialog
        open={dialog.open}
        mode={dialog.mode}
        vehicle={dialog.vehicle}
        customerId={customerId}
        onClose={closeDialog}
        onSaved={() => refetch()}
      />
    </Box>
  );
};

export default MobilityVehiclesContent;

const useSxStyles = (theme: Theme): Record<string, SxProps<Theme>> => ({
  titleText: {
    lineHeight: 1,
    fontSize: 24,
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: { fontSize: 21 },
  },
  emptyFiltered: {
    padding: theme.spacing(5, 2),
    textAlign: 'center',
  },
  emptyZero: {
    padding: theme.spacing(5, 3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  emptyBadge: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'secondaryAccent.50',
    color: 'secondaryAccent.main',
  },
});
